import { img } from '@/assets';
import { BottomStoreInfo } from '@/components/common/bottomStoreInfo';
import { Privacy } from '@/components/common/privacy';
import { Style } from '@/components/style';
import { availableLocales } from '@/constants/locale';
import Area from '@/utils/area';
import ManifestSettings from '@/utils/manifest';
import Validator from '@/utils/validator';
import { setErrorMessage } from '@actions/message/messageAction';
import * as WelcomeAction from '@actions/welcome/welcomeAction';
import { ResponseCode } from '@services/responseCode';
import DeliveryType from '@/constants/DeliveryType';
import DeliveryMethodType from '@/constants/DeliveryMethodType';
import * as UberDirectAction from '@/actions/uberDirect/uberDirectAPI';
import {
  setDeliveryNameError,
  setDeliveryPhoneError,
  setSelectArea,
  setSelectCity,
  setUberDirectApiError,
} from '@/actions/uberDirect/uberDirectAction';
import { TW_address } from '@/constants/Address';
import UberDirect from '@/utils/uberDirect';
import { parsePhoneNumber } from 'libphonenumber-js/max';
import { cloneDeep, forEach, head, last } from 'lodash';
import moment from 'moment';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { FormattedMessage, useIntl } from 'react-intl';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import ServeType from '@/constants/ServeType';
import './welcome.scss';
import ErrorMsgUtils from '@/utils/errorMsgUtils';
import WelcomeUtils from '@/utils/welcomeUtils';
import WelcomeServiceSuspended from '@/ui/ServiceSuspended/WelcomeServiceSuspended';
import CustomizeWelcome from '../customizeWelcome';
import {
  AddressDetails,
  AddressInput,
  AddressSelector,
  CommentInput,
  DeliveryMethodSelector,
  LocationSelector,
  NameInput,
  PhoneInput,
  ServeTypeRow,
  StoreNotOpenRemind,
  TimeRemind,
  TimeSelector,
} from './components';

const { alertColor, blackColor, primaryColor } = new Style().getUiConfig();

type Props = {
  locale: string;
  setLocale: (locale: string) => void;
  isTest?: boolean;
};

type Params = { storeUuid: string };

type OrderServeType =
  | 'DEFAULT'
  | 'DINE_IN'
  | 'TAKE_OUT'
  | 'SCHEDULE_TAKE_OUT'
  | 'DELIVERY'
  | 'PICK_UP'
  | 'PAY';

type CreateOrderType = 'NEW' | 'EXISTING' | 'ADDON';

function Welcome({ locale, setLocale, isTest }: Props) {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const style = new Style();
  const dispatch = useDispatch();

  const phoneInput = useRef<HTMLInputElement>(
    null
  ) as MutableRefObject<HTMLInputElement>;
  const employeeInput = useRef<HTMLInputElement>(
    null
  ) as MutableRefObject<HTMLInputElement>;
  const nameInput = useRef<HTMLInputElement>(
    null
  ) as MutableRefObject<HTMLInputElement>;
  const addressInput = useRef<HTMLInputElement>(
    null
  ) as MutableRefObject<HTMLInputElement>;
  const commentInput = useRef<HTMLInputElement>(
    null
  ) as MutableRefObject<HTMLInputElement>;

  const mode: string | null = new URLSearchParams(location.search).get('mode');
  const phone: string | null = new URLSearchParams(location.search).get(
    'phone'
  );
  const customUuid: string | null = new URLSearchParams(location.search).get(
    'customUuid'
  );
  const welcomeLocale: string | null = new URLSearchParams(location.search).get(
    'welcomeLocale'
  );
  const redirectToken: string | null = new URLSearchParams(location.search).get(
    'redirectToken'
  );

  const { storeUuid } = useParams<Params>();
  const [currentStoreLocale, setCurrentStoreLocale] = useState<any>();
  const [storeLocales, setStoreLocales] = useState<any[]>([]);
  const [selectedTakeOutServeType, setSelectedTakeOutServeType] =
    useState<OrderServeType>('DEFAULT');
  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();
  const [excludeDates, setExcludeDates] = useState<Date[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState<string | undefined>();
  const [shareConditionInfo, setShareConditionInfo] = useState<any>({});
  const [tradingLocation, setTradingLocation] = useState<any>('');
  const [deliveryType, setDeliveryType] = useState<boolean>(true);
  const [name, setName] = useState<string | undefined>(undefined);
  const [nameInputRequired, setNameInputRequired] = useState<boolean>(false);
  const [ctaBtnStyle, setCtaBtnStyle] = useState<boolean>(true);
  const [ctaBtnText, setCTABtnText] = useState<boolean>(true);
  const [selectedAreaCode, setSelectedAreaCode] = useState<any>();
  const [areaCodes, setAreaCodes] = useState<any>([]);
  const [timeInfos, setTimeInfos] = useState<any[]>([]);
  const [deliveryMethod, setDeliveryMethod] = useState<boolean>(true);
  const [city, setCity] = useState<string | null>(null);
  const [area, setArea] = useState<string | null>(null);
  const [street, setStreet] = useState<string | null>(null);
  const [serviceSuspendedRes, setServiceSuspendedRes] = useState({});
  // fix welcome
  const [showCustomizeWelcome, setShowCustomizeWelcome] = useState<boolean>(
    true // !!customUuid
  );
  const webStoreVoResponse = useSelector(
    (state: RootStateOrAny) => state.welcomeReducer.webStoreVoResponse
  );
  const selectedServeType = useSelector<RootStateOrAny, any>(
    (state) => state.modeReducer.serveType
  );
  const customizedServeType = useSelector<RootStateOrAny, any>(
    (state) => state.modeReducer.customizedServeType
  );
  ManifestSettings.setPWAurl();

  const getDeliveryType = (type) => {
    if (type === DeliveryType.UBER_DIRECT) {
      setDeliveryType(true);
      return;
    }
    setDeliveryType(false);
  };

  const submitOrder = (webVo: any) => {
    if (isTest) {
      return;
    }

    WelcomeAction.post(storeUuid, webVo).then((res: any) => {
      history.push(`${process.env.REACT_APP_VERSION}${res.data.route}`);
    });
  };

  const getWebVo = () => {
    return cloneDeep(webStoreVoResponse.data);
  };

  useEffect(() => {
    if (isTest) {
      return;
    }
    dispatch(
      WelcomeAction.welcome(storeUuid, mode, phone, customUuid, redirectToken)
    );
  }, []);

  const initAreaData = (cityName) => {
    const areas = cloneDeep(
      TW_address.filter((data) => data.name === cityName)[0].districts
    );
    const tempAreaData = areas.map((data) => {
      return data.name;
    });
    return tempAreaData;
  };
  useEffect(() => {
    const cityData = TW_address.map((data) => {
      return data.name;
    });
    const selectCity = cityData[0];
    setCity(selectCity);
    dispatch(setSelectCity(selectCity));
    const selectArea = initAreaData(selectCity)[0];
    setArea(selectArea);
    dispatch(setSelectArea(selectArea));
  }, []);

  useEffect(() => {
    if (!webStoreVoResponse?.data) {
      return () => {};
    }

    if (webStoreVoResponse.code !== ResponseCode.NO_ERROR) {
      if (webStoreVoResponse.code === ResponseCode.STORE_NOT_OPEN) {
        if (
          !WelcomeUtils.skipStoreNotOpenDiolog(
            webStoreVoResponse?.data?.webStoreVo?.availableServeTypes,
            webStoreVoResponse?.data?.webStoreVo?.availableTakeOutServeTypes
          )
        ) {
          setErrorMessage(
            intl.formatMessage(
              { id: `error.${webStoreVoResponse.code}.msg` },
              {
                startTime: webStoreVoResponse?.data?.webStoreVo?.startTime,
                endTime: webStoreVoResponse?.data?.webStoreVo?.endTime,
              }
            ),
            {
              title: intl.formatMessage({
                id: `error.${webStoreVoResponse.code}.title`,
              }),
              titleClass: 'justify-content-center',
              bodyClass: 'text-center',
            }
          );
        }
      } else if (webStoreVoResponse.code === ResponseCode.STORE_OFF_LINE) {
        setErrorMessage(intl.formatMessage({ id: 'error.50003' }));
        setServiceSuspendedRes(webStoreVoResponse);
      } else {
        const errorData: any = ErrorMsgUtils.translateErrorCode(
          String(webStoreVoResponse.code),
          intl
        );
        setErrorMessage(errorData.msg, {
          title: errorData.title,
        });
      }
      if (webStoreVoResponse.code !== ResponseCode.STORE_NOT_OPEN)
        return () => {};
    }

    const allLangs = webStoreVoResponse.data.webStoreVo.langs;
    const areaCodeList = Area.getAreaCodesListByLangs(allLangs);
    const storeLocale = Area.getLocaleListByLangs(allLangs);
    const defaultLocale = Object.keys(storeLocale[0])[0]; // ex : zh-TW
    const defaultStoreLocale = storeLocale[0][Object.keys(storeLocale[0])[0]];
    const defaultAreaCode = areaCodeList[0];

    getDeliveryType(webStoreVoResponse.data.webStoreVo.deliveryPlatform);

    setStoreLocales(storeLocale);
    if (locale) {
      const findCurrentStoreLocale = storeLocale.find(
        (sl) => Object.keys(sl)[0] === locale
      );
      if (findCurrentStoreLocale) {
        if (welcomeLocale === null) {
          const queryParams = new URLSearchParams(location.search);
          queryParams.set('welcomeLocale', String(defaultLocale));
          setLocale(defaultLocale);
          history.push({
            pathname: location.pathname,
            search: `${queryParams}`,
          });
        } else if (Area.checkAreaCodeExist(welcomeLocale)) {
          setLocale(welcomeLocale);
        }
        setCurrentStoreLocale(findCurrentStoreLocale[locale]);
      } else {
        setLocale(defaultLocale);
        setCurrentStoreLocale(defaultStoreLocale);
      }
    } else {
      setLocale(defaultLocale);
      setCurrentStoreLocale(defaultStoreLocale);
    }

    setSelectedAreaCode(defaultAreaCode);
    setAreaCodes(areaCodeList);

    const sConditionInfo = cloneDeep(shareConditionInfo);
    webStoreVoResponse.data.webStoreVo.shareConditionInfo =
      webStoreVoResponse.data.webStoreVo.shareConditionInfo.map(
        (info: any, idx) => {
          const newInfo = cloneDeep(info);
          let storeLocaleIdx = -1;
          storeLocale.forEach((slocale, i) => {
            Object.keys(slocale).forEach((key) => {
              if (key === locale) {
                storeLocaleIdx = i;
              }
            });
          });

          if (storeLocaleIdx !== -1) {
            const titles =
              newInfo.titles.transMap[storeLocale[storeLocaleIdx][locale].lang];
            const descs =
              newInfo.descs.transMap[storeLocale[storeLocaleIdx][locale].lang];

            if (!titles) {
              newInfo.title = titles;
            }
            if (!descs) {
              newInfo.desc = descs;
            }
            newInfo.idx = idx;
            sConditionInfo[newInfo.id] = newInfo;
          }

          return newInfo;
        }
      );

    setShareConditionInfo(sConditionInfo);

    if (mode === 'pickup' && webStoreVoResponse.data.webStoreVo.takeoutEnable) {
      const webVo = getWebVo();
      webVo.locale = defaultStoreLocale.lang;
      const newOrderVo = cloneDeep(webStoreVoResponse.data.orderVo);
      newOrderVo.serveType = 'TAKE_OUT';
      newOrderVo.menuLocale = defaultStoreLocale.lang;
      webVo.orderVo = newOrderVo;
      webVo.orderGroupVo = null;
      submitOrder(webVo);
    } else if (
      mode === 'pickup' &&
      !webStoreVoResponse.data.webStoreVo.takeoutEnable
    ) {
      setErrorMessage(intl.formatMessage({ id: 'error.pickup.unavailable' }));
    }

    return () => {};
  }, [webStoreVoResponse]);

  const isNameInputRequired = (): void => {
    if (selectedServeType === ServeType.DELIVERY) {
      setNameInputRequired(true);
      return;
    }

    const takeoutNameInputRequired =
      webStoreVoResponse.data.webStoreVo.webUiConfig?.enableInputName;
    if (
      selectedServeType === ServeType.TAKE_OUT &&
      selectedTakeOutServeType === ServeType.TAKE_OUT &&
      takeoutNameInputRequired
    ) {
      setNameInputRequired(true);
      return;
    }
    setNameInputRequired(false);
  };

  useEffect(() => {
    if (webStoreVoResponse && webStoreVoResponse.data) {
      setSelectedDate(undefined);
      setSelectedTime(undefined);
      setExcludeDates([]);

      isNameInputRequired();

      const { preorderTimeMap } = webStoreVoResponse.data.webStoreVo;
      const serveAvailableDates =
        preorderTimeMap[selectedServeType]?.availableDates;

      if (selectedServeType === 'DELIVERY' && serveAvailableDates.length > 0) {
        const minDateString = head(serveAvailableDates) || null;
        setMinDate(moment(minDateString, 'YYYY-MM-DD').toDate());

        const maxDateString = last(serveAvailableDates) || null;
        setMaxDate(moment(maxDateString, 'YYYY-MM-DD').toDate());

        let current;
        const newExcludeDates: Date[] = [];

        forEach(serveAvailableDates, (availableDate) => {
          if (current == null) {
            current = moment(availableDate).format('YYYY-MM-DD');
          }
          const da: any = moment(availableDate).format('YYYY-MM-DD');
          while (current !== da) {
            newExcludeDates.push(new Date(current));
            current = moment(moment(current).add(1, 'd')).format('YYYY-MM-DD');
          }
          current = moment(moment(current).add(1, 'd')).format('YYYY-MM-DD');
        });

        setExcludeDates(newExcludeDates);

        let selected = false;

        forEach(serveAvailableDates, (availableDate): boolean => {
          if (selected) {
            return false;
          }

          const selectDateString = availableDate;

          setSelectedDate(moment(selectDateString, 'YYYY-MM-DD').toDate());

          let selectedKey;
          Object.keys(preorderTimeMap[selectedServeType].availableTimes).some(
            (key) => {
              if (key.includes(moment(selectDateString).format('yyyy-MM-DD'))) {
                selectedKey = key;
                return true;
              }
              return false;
            }
          );

          setAvailableTimes([]);
          setTimeInfos([]);
          let times: any = [];
          let newTimeInfos: any = [];
          preorderTimeMap[selectedServeType].availableTimes[
            selectedKey
          ].forEach((time) => {
            times = [...times, time];
            setAvailableTimes((availableTime) => [...availableTime, time]);
          });

          preorderTimeMap[selectedServeType].timeInfos[selectedKey]?.forEach(
            (info) => {
              newTimeInfos = [...newTimeInfos, info];
              setTimeInfos((oldTimeInfos) => [...oldTimeInfos, info]);
            }
          );

          forEach(times, (time) => {
            let timeInfo;
            newTimeInfos.some((info) => {
              if (info.time === time) {
                timeInfo = info;
                return true;
              }
              return false;
            });

            const disabled: any = timeInfo?.full ? 'disabled' : undefined;
            if (disabled === undefined) {
              selected = true;
              setSelectedTime(time);
              return false;
            }
            return true;
          });

          return true;
        });
      }

      const takeOutServeAvailableDates =
        preorderTimeMap[selectedTakeOutServeType]?.availableDates;

      if (
        webStoreVoResponse.data.webStoreVo.availableTakeOutServeTypes
          ?.length === 1
      ) {
        setSelectedTakeOutServeType(
          webStoreVoResponse.data.webStoreVo.availableTakeOutServeTypes[0]
        );
      }

      if (webStoreVoResponse.data.webStoreVo.tradingLocations?.length === 1) {
        setTradingLocation(
          webStoreVoResponse.data.webStoreVo.tradingLocations[0]
        );
      }

      if (
        selectedServeType === 'TAKE_OUT' &&
        selectedTakeOutServeType === 'SCHEDULE_TAKE_OUT' &&
        takeOutServeAvailableDates.length > 0
      ) {
        const minDateString = head(takeOutServeAvailableDates) || null;
        setMinDate(moment(minDateString, 'YYYY-MM-DD').toDate());

        const maxDateString = last(takeOutServeAvailableDates) || null;

        setMaxDate(moment(maxDateString, 'YYYY-MM-DD').toDate());

        let current;
        const newExcludeDates: Date[] = [];

        forEach(takeOutServeAvailableDates, (availableDate) => {
          if (current == null) {
            current = moment(availableDate).format('YYYY-MM-DD');
          }
          const da: any = moment(availableDate).format('YYYY-MM-DD');
          while (current !== da) {
            newExcludeDates.push(new Date(current));
            current = moment(moment(current).add(1, 'd')).format('YYYY-MM-DD');
          }
          current = moment(moment(current).add(1, 'd')).format('YYYY-MM-DD');
        });

        setExcludeDates(newExcludeDates);

        let selected = false;
        forEach(takeOutServeAvailableDates, (availableDate) => {
          if (selected) {
            return false;
          }

          const selectedDateString = availableDate;
          setSelectedDate(moment(selectedDateString, 'YYYY-MM-DD').toDate());

          let selectedKey;
          Object.keys(
            preorderTimeMap[selectedTakeOutServeType].availableTimes
          ).some((key) => {
            if (key.includes(moment(selectedDateString).format('yyyy-MM-DD'))) {
              selectedKey = key;
              return true;
            }
            return false;
          });
          setAvailableTimes([]);
          setTimeInfos([]);

          let times: any = [];
          let newTimeInfos: any = [];
          preorderTimeMap[selectedTakeOutServeType].availableTimes[
            selectedKey
          ].forEach((time) => {
            times = [...times, time];
            setAvailableTimes((availableTime) => [...availableTime, time]);
          });

          preorderTimeMap[selectedTakeOutServeType].timeInfos[
            selectedKey
          ]?.forEach((info) => {
            newTimeInfos = [...newTimeInfos, info];
            setTimeInfos((oldTimeInfos) => [...oldTimeInfos, info]);
          });

          forEach(times, (time) => {
            let timeInfo;
            newTimeInfos.some((info) => {
              if (info.time === time) {
                timeInfo = info;
                return true;
              }
              return false;
            });

            const disabled: any = timeInfo?.full ? 'disabled' : undefined;
            if (disabled === undefined) {
              selected = true;
              setSelectedTime(time);
              return false;
            }

            return true;
          });

          return true;
        });
      }
    }

    if (document.querySelector('.react-datepicker__input-container > input')) {
      const a: any = document.querySelector(
        '.react-datepicker__input-container > input'
      );
      a.setAttribute('readonly', 'readonly');
      a.setAttribute('data-testid', 'date');
    }

    return () => {};
  }, [
    webStoreVoResponse,
    storeLocales,
    selectedServeType,
    selectedTakeOutServeType,
  ]);

  const Components = {
    DisplayParty: () => {
      if (
        Object.keys(shareConditionInfo).length === 0 ||
        Object.keys(shareConditionInfo).some((key) => {
          if (shareConditionInfo[key].count > 0) {
            return true;
          }
          return false;
        })
      ) {
        let text = '';
        Object.entries(shareConditionInfo)
          .sort(([i, a], [j, b]) => {
            const x: any = a;
            const y: any = b;
            return x.idx - y.idx;
          })
          .forEach(([key, a]) => {
            text += `${shareConditionInfo[key].title}: ${shareConditionInfo[key].count}, `;
          });
        return <div>{text.substring(0, text.length - 2)}</div>;
      }
      return <FormattedMessage id="welcome.setParty" />;
    },
  };

  const isInBusinessHours = (): boolean => {
    const date = new Date();
    const businessHoursCrossDay =
      moment(webStoreVoResponse.data.webStoreVo.endTime, 'HH:mm').diff(
        moment(webStoreVoResponse.data.webStoreVo.startTime, 'HH:mm')
      ) <= 0
        ? 1
        : 0;
    const startTime =
      moment(date).format('YYYY-MM-DDT').toString() +
      webStoreVoResponse.data.webStoreVo.startTime;

    const endTime =
      moment(date)
        .add(businessHoursCrossDay, 'days')
        .format('YYYY-MM-DDT')
        .toString() + webStoreVoResponse.data.webStoreVo.endTime;

    const format = 'YYYY-MM-DDTHH:mm';
    let inBusinessHours = moment(date, format).isBetween(
      moment(startTime, format),
      moment(endTime, format)
    );

    if (!inBusinessHours && businessHoursCrossDay === 1) {
      const midNight = new Date();
      midNight.setHours(0);
      midNight.setHours(0);
      midNight.setSeconds(0);
      const earlyMoringEndTime =
        moment(date).format('YYYY-MM-DDT').toString() +
        webStoreVoResponse.data.webStoreVo.endTime;
      inBusinessHours = moment(date, format).isBetween(
        moment(midNight, format),
        moment(earlyMoringEndTime, format),
        null,
        '[]'
      );
    }

    return inBusinessHours;
  };

  const isViewMenuValid = (): boolean => {
    const date = new Date();
    const menuSchedules = JSON.parse(
      JSON.stringify(webStoreVoResponse.data.webStoreVo.menuSchedules)
    );

    let isMenuView = false;

    for (let i = 0; i < menuSchedules.length; i += 1) {
      // check formdate
      const fromDate = moment(menuSchedules[i].fromDate);
      const toDate = moment(menuSchedules[i].toDate);
      const isDate = moment(date).isBetween(moment(fromDate), moment(toDate));
      if (!isDate) continue;

      // check week
      const dayOfWeek = JSON.parse(JSON.stringify(menuSchedules[i].dayOfWeek));
      const isDayOfWeek = dayOfWeek?.includes(date.getDay());
      if (!isDayOfWeek) continue;

      // check time
      const businessHoursCrossDay =
        moment(menuSchedules[i].dailyToTime, 'HH:mm').diff(
          moment(menuSchedules[i].dailyFromTime, 'HH:mm')
        ) <= 0
          ? 1
          : 0;
      const dailyFromTime =
        moment(date).format('YYYY-MM-DDT').toString() +
        menuSchedules[i].dailyFromTime;
      const dailyToTime =
        moment(date)
          .add(businessHoursCrossDay, 'days')
          .format('YYYY-MM-DDT')
          .toString() + menuSchedules[i].dailyToTime;
      const format = 'YYYY-MM-DDTHH:mm';
      const isDailyTime = moment(date, format).isBetween(
        moment(dailyFromTime, format),
        moment(dailyToTime, format)
      );

      if (isDailyTime) {
        isMenuView = true;
        break;
      }
    }
    return isMenuView;
  };

  // welcome 外送 CTA button 檢查 輸入匡 isValid
  const isDeliveryFormValid = () => {
    if (customizedServeType === 'EMPLOYEE') return true;

    const phoneValid = Validator.phoneValidator(
      phoneInput.current?.value,
      selectedAreaCode?.iti,
      true
    );

    const nameValid = Validator.nameValidator(nameInput.current?.value, true);
    const addressValid =
      Validator.addressValidator(addressInput.current?.value, true) ||
      tradingLocation;

    if (phoneValid && nameValid && addressValid) {
      return true;
    }
    return false;
  };

  const checkCTABtnStatus = (): void => {
    if (selectedServeType === ServeType.DEFAULT) return;

    if (selectedServeType === ServeType.DINE_IN) {
      if (!isInBusinessHours()) {
        setCtaBtnStyle(false);
        setCTABtnText(true);
        return;
      }
    }

    if (selectedServeType === ServeType.TAKE_OUT) {
      if (selectedTakeOutServeType === ServeType.DEFAULT) {
        setCtaBtnStyle(false);
        setCTABtnText(true);
        return;
      }

      if (selectedTakeOutServeType === ServeType.TAKE_OUT) {
        if (!isInBusinessHours()) {
          if (!isViewMenuValid()) {
            setCtaBtnStyle(false);
            setCTABtnText(true);
            return;
          }
          setCTABtnText(false);
        }
      }

      if (selectedTakeOutServeType === ServeType.SCHEDULE_TAKE_OUT) {
        setCTABtnText(true);
      }
    }

    if (selectedServeType === ServeType.DELIVERY) {
      if (!isDeliveryFormValid()) {
        setCtaBtnStyle(false);
        return;
      }
    }

    setCtaBtnStyle(true);
  };

  useEffect(() => {
    if (!webStoreVoResponse?.data) {
      return;
    }
    checkCTABtnStatus();
  }, [
    selectedServeType,
    selectedTakeOutServeType,
    phoneInput.current?.value,
    nameInput.current?.value,
    addressInput.current?.value,
    tradingLocation,
  ]);

  const showStoreNotOpenRemind = (): boolean => {
    let showFlag = false;

    if (ctaBtnStyle) {
      if (!ctaBtnText) {
        showFlag = true;
      }
      return showFlag;
    }

    if (selectedServeType === ServeType.DINE_IN) {
      showFlag = true;
      return showFlag;
    }

    if (selectedTakeOutServeType === ServeType.TAKE_OUT) {
      showFlag = true;
      return showFlag;
    }

    return showFlag;
  };

  const isPartyValid = () => {
    if (
      Object.keys(shareConditionInfo).length === 0 ||
      Object.keys(shareConditionInfo).some((key) => {
        if (shareConditionInfo[key].count > 0) {
          return true;
        }
        return false;
      })
    ) {
      return true;
    }
    setErrorMessage(intl.formatMessage({ id: 'error.noParty' }));
    return false;
  };

  // check input name is not empty and check fill name option is open or not
  const isNameValid = (required: boolean) => {
    const isVaild = Validator.nameValidator(nameInput.current?.value, required);
    if (!isVaild) {
      setErrorMessage(intl.formatMessage({ id: 'error.invalidName' }));
    }
    return isVaild;
  };

  const isPhoneValid = (required: boolean) => {
    if (customizedServeType === 'EMPLOYEE') return true;
    const isValid = Validator.phoneValidator(
      phoneInput.current?.value,
      selectedAreaCode.iti,
      required
    );
    if (!isValid) {
      setErrorMessage(intl.formatMessage({ id: 'error.invalidMobile' }));
    }
    return isValid;
  };

  // CKS-4765 => 第一個為英文即可（不看大小寫），後面為數字，不限制長度。
  const isEmployeeIdValid = () => {
    if (customizedServeType !== 'EMPLOYEE') return true;
    const isVaild = Validator.employeeValidator(employeeInput.current?.value);
    if (!isVaild) {
      setErrorMessage(intl.formatMessage({ id: 'phison.employee.id.error' }));
    }
    return isVaild;
  };

  const isDateTimeValid = () => {
    if (selectedDate === undefined || selectedTime === undefined) {
      setErrorMessage(intl.formatMessage({ id: 'error.invalidDateTime' }));
      return false;
    }
    return true;
  };

  const isDeliveryFieldValid = () => {
    const nameValid = Validator.nameValidator(nameInput.current?.value, true);
    const addressValid =
      Validator.addressValidator(addressInput.current?.value, true) ||
      tradingLocation;
    if (nameValid && addressValid) {
      return true;
    }
    setErrorMessage(
      intl.formatMessage({ id: 'error.invalidNameAddressFields' })
    );
    return false;
  };

  const getCustomerAddress = () => {
    if (selectedServeType === 'DELIVERY' && deliveryType) {
      if (city && area && street) {
        const tempStructAddress = city.concat(area.concat(street));
        return tempStructAddress;
      }
    }

    if (addressInput?.current?.value) return addressInput.current.value;
    return tradingLocation;
  };

  const getStructAddress = () => {
    if (selectedServeType !== 'DELIVERY') return null;
    if (!deliveryType) return null;
    const tempStructAddress =
      UberDirect.uberDirectStructAddress.createStructAddress(
        String(city),
        String(area),
        String(street)
      );
    return tempStructAddress;
  };

  const getUberDeliveryMethod = () => {
    if (deliveryMethod) {
      return DeliveryMethodType.deliverable_action_meet_at_door;
    }
    return DeliveryMethodType.deliverable_action_leave_at_door;
  };

  const fixedEncodeURI = (str) => {
    return encodeURI(str);
  };

  const uberDirectAddressValidator = async () => {
    if (selectedServeType !== 'DELIVERY') return true;
    if (!deliveryType) return true;
    if (!storeUuid) return false;

    const checkPhoneValid = Validator.phoneValidator(
      phoneInput.current?.value,
      selectedAreaCode.iti,
      true
    );
    if (!checkPhoneValid) dispatch(setDeliveryPhoneError('phone'));

    const isNameVaild = Validator.nameValidator(nameInput.current?.value, true);
    if (!isNameVaild) dispatch(setDeliveryNameError('name'));

    if (!addressInput?.current?.value) {
      dispatch(setUberDirectApiError('address_empty'));
      return false;
    }

    let result = false;

    const tempAddress = fixedEncodeURI(getStructAddress());

    try {
      const res = await UberDirectAction.uberDeliveryValidate(
        storeUuid,
        String(tempAddress)
      );
      const kind = res?.data?.kind ? cloneDeep(res.data.kind) : null;
      if (kind === 'delivery_quote') {
        result = true;
      } else {
        dispatch(setUberDirectApiError(res));
      }
    } catch (e) {
      console.log('error', e);
    }
    return result;
  };

  const validateOrder = async (createOrderType: CreateOrderType) => {
    if (!(await uberDirectAddressValidator())) {
      return;
    }

    if (
      webStoreVoResponse &&
      !webStoreVoResponse.data.webStoreVo.isRoyaltyEnable
    ) {
      let storeLocaleIdx = -1;
      storeLocales.forEach((slocale, i) => {
        Object.keys(slocale).forEach((key) => {
          if (key === locale) {
            storeLocaleIdx = i;
          }
        });
      });
      const webVo = getWebVo();
      webVo.locale = storeLocales[storeLocaleIdx][locale].lang;
      const newOrderVo = cloneDeep(webStoreVoResponse.data.orderVo);
      if (createOrderType === 'NEW') {
        newOrderVo.orderType = 'NEW';
        if (
          selectedServeType === 'DINE_IN' &&
          isPartyValid() &&
          isPhoneValid(
            webStoreVoResponse.data.webStoreVo.webUiConfig?.enableInputPhone
          )
        ) {
          if (webStoreVoResponse.data.webStoreVo.isPostPayEnable) {
            if (phoneInput.current?.value) {
              const phoneNumber =
                selectedAreaCode.areaCode + phoneInput.current.value;
              const parsePhone = parsePhoneNumber(
                phoneNumber,
                selectedAreaCode.iti
              );
              newOrderVo.table.mobile = parsePhone
                .formatInternational()
                .replaceAll(' ', '');
            }
            newOrderVo.table.country = selectedAreaCode.country;
          }

          newOrderVo.serveType = 'DINE_IN';

          if (phoneInput.current?.value) {
            const phoneNumber =
              selectedAreaCode.areaCode + phoneInput.current.value;
            const parsePhone = parsePhoneNumber(
              phoneNumber,
              selectedAreaCode.iti
            );
            newOrderVo.table.mobile = parsePhone
              .formatInternational()
              .replaceAll(' ', '');
            newOrderVo.table.country = selectedAreaCode.country;
          }
          Object.keys(shareConditionInfo).forEach((key) => {
            newOrderVo.conditions[shareConditionInfo[key].id] =
              shareConditionInfo[key].count;
          });
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });

          if (ctaBtnStyle) {
            submitOrder(webVo);
          }
          return;
        }
        if (
          selectedServeType === 'TAKE_OUT' &&
          selectedTakeOutServeType === 'TAKE_OUT' &&
          isPhoneValid(
            webStoreVoResponse.data.webStoreVo.webUiConfig?.enableTakeoutPhone
          ) &&
          isNameValid(
            webStoreVoResponse.data.webStoreVo.webUiConfig?.enableInputName
          )
        ) {
          if (!isEmployeeIdValid()) return;
          newOrderVo.serveType = 'TAKE_OUT';
          if (phoneInput.current?.value) {
            const phoneNumber =
              selectedAreaCode.areaCode + phoneInput.current.value;
            const parsePhone = parsePhoneNumber(
              phoneNumber,
              selectedAreaCode.iti
            );
            newOrderVo.table.mobile = parsePhone
              .formatInternational()
              .replaceAll(' ', '');
            newOrderVo.table.country = selectedAreaCode.country;
          }
          if (customizedServeType === 'EMPLOYEE') {
            const extensionNumber: any =
              document.getElementById('extensionNumber');
            newOrderVo.common1 = employeeInput.current.value;
            newOrderVo.common2 = extensionNumber.value;
          }

          if (
            nameInput.current?.value &&
            (webStoreVoResponse.data.webStoreVo.webUiConfig?.showInputName ||
              webStoreVoResponse.data.webStoreVo.webUiConfig?.enableInputName)
          ) {
            newOrderVo.customerName = nameInput.current.value;
          }
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          if (ctaBtnStyle) {
            submitOrder(webVo);
          }
          return;
        }
        if (
          selectedServeType === 'TAKE_OUT' &&
          selectedTakeOutServeType === 'SCHEDULE_TAKE_OUT' &&
          isPhoneValid(true) &&
          isDateTimeValid()
        ) {
          if (!isEmployeeIdValid()) return;
          newOrderVo.serveType = 'TAKE_OUT';
          if (customizedServeType !== 'EMPLOYEE') {
            const phoneNumber =
              selectedAreaCode.areaCode + phoneInput.current.value;
            const parsePhone = parsePhoneNumber(
              phoneNumber,
              selectedAreaCode.iti
            );
            newOrderVo.table.mobile = parsePhone
              .formatInternational()
              .replaceAll(' ', '');
          }
          if (customizedServeType === 'EMPLOYEE') {
            const extensionNumber: any =
              document.getElementById('extensionNumber');
            newOrderVo.common1 = employeeInput.current.value;
            newOrderVo.common2 = extensionNumber.value;
          }
          newOrderVo.table.country = selectedAreaCode.country;
          newOrderVo.preorderTime = selectedTime;
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          submitOrder(webVo);
          return;
        }
        if (
          selectedServeType === 'DELIVERY' &&
          isPhoneValid(true) &&
          isDeliveryFieldValid() &&
          isDateTimeValid()
        ) {
          newOrderVo.serveType = 'DELIVERY';

          const phoneNumber =
            selectedAreaCode.areaCode + phoneInput.current.value;
          const parsePhone = parsePhoneNumber(
            phoneNumber,
            selectedAreaCode.iti
          );
          newOrderVo.table.mobile = parsePhone
            .formatInternational()
            .replaceAll(' ', '');
          newOrderVo.table.country = selectedAreaCode.country;
          newOrderVo.customerName = nameInput.current.value;
          newOrderVo.customerAddress = getCustomerAddress();
          if (
            webStoreVoResponse.data.webStoreVo.webUiConfig?.showRequireComments
          ) {
            if (commentInput.current?.value.length > 0) {
              newOrderVo.comment = commentInput.current.value;
            }
          }
          newOrderVo.preorderTime = selectedTime;
          newOrderVo.deliverableAction = getUberDeliveryMethod();
          newOrderVo.structuredAddress = getStructAddress();
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          if (ctaBtnStyle) {
            submitOrder(webVo);
          }

          return;
        }
      }
      if (createOrderType === 'EXISTING') {
        newOrderVo.orderType = 'EXISTING';
        if (selectedServeType === 'DINE_IN' && isPhoneValid(true)) {
          newOrderVo.serveType = 'DINE_IN';

          const phoneNumber =
            selectedAreaCode.areaCode + phoneInput.current.value;
          const parsePhone = parsePhoneNumber(
            phoneNumber,
            selectedAreaCode.iti
          );
          newOrderVo.table.mobile = parsePhone
            .formatInternational()
            .replaceAll(' ', '');
          newOrderVo.table.country = selectedAreaCode.country;
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          submitOrder(webVo);
          return;
        }
      }
      if (createOrderType === 'ADDON') {
        newOrderVo.orderType = 'ADDON';
        Object.keys(shareConditionInfo).forEach((key) => {
          newOrderVo.conditions[shareConditionInfo[key].id] =
            shareConditionInfo[key].count;
        });
        if (selectedServeType === 'DINE_IN') {
          newOrderVo.serveType = 'DINE_IN';
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          submitOrder(webVo);
        }
      }
    } else if (
      webStoreVoResponse &&
      webStoreVoResponse.data.webStoreVo.isRoyaltyEnable
    ) {
      let storeLocaleIdx = -1;
      storeLocales.forEach((slocale, i) => {
        Object.keys(slocale).forEach((key) => {
          if (key === locale) {
            storeLocaleIdx = i;
          }
        });
      });
      const webVo = getWebVo();
      webVo.locale = storeLocales[storeLocaleIdx][locale].lang;
      const newOrderVo = cloneDeep(webStoreVoResponse.data.orderVo);
      if (createOrderType === 'NEW') {
        newOrderVo.orderType = 'NEW';
        if (
          selectedServeType === 'DINE_IN' &&
          isPartyValid() &&
          isPhoneValid(
            webStoreVoResponse.data.webStoreVo.webUiConfig.enableInputPhone ||
              webStoreVoResponse.data.webStoreVo.isPostPayEnable
          )
        ) {
          if (webStoreVoResponse.data.webStoreVo.isPostPayEnable) {
            if (phoneInput.current.value) {
              const phoneNumber =
                selectedAreaCode.areaCode + phoneInput.current.value;
              const parsePhone = parsePhoneNumber(
                phoneNumber,
                selectedAreaCode.iti
              );
              newOrderVo.table.mobile = parsePhone
                .formatInternational()
                .replaceAll(' ', '');
              newOrderVo.table.country = selectedAreaCode.country;
            }
          }

          newOrderVo.serveType = 'DINE_IN';
          if (phoneInput.current?.value) {
            const phoneNumber =
              selectedAreaCode.areaCode + phoneInput.current.value;
            const parsePhone = parsePhoneNumber(
              phoneNumber,
              selectedAreaCode.iti
            );
            newOrderVo.table.mobile = parsePhone
              .formatInternational()
              .replaceAll(' ', '');
            newOrderVo.table.country = selectedAreaCode.country;
          }
          Object.keys(shareConditionInfo).forEach((key) => {
            newOrderVo.conditions[shareConditionInfo[key].id] =
              shareConditionInfo[key].count;
          });
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          if (ctaBtnStyle) {
            submitOrder(webVo);
          }
          // submitOrder(webVo);
          return;
        }
        if (
          selectedServeType === 'TAKE_OUT' &&
          selectedTakeOutServeType === 'TAKE_OUT' &&
          isPhoneValid(
            webStoreVoResponse.data.webStoreVo.webUiConfig?.enableTakeoutPhone
          ) &&
          isNameValid(
            webStoreVoResponse.data.webStoreVo.webUiConfig?.enableInputName
          )
        ) {
          newOrderVo.serveType = 'TAKE_OUT';
          if (phoneInput.current?.value) {
            const phoneNumber =
              selectedAreaCode.areaCode + phoneInput.current.value;
            const parsePhone = parsePhoneNumber(
              phoneNumber,
              selectedAreaCode.iti
            );
            newOrderVo.table.mobile = parsePhone
              .formatInternational()
              .replaceAll(' ', '');
            newOrderVo.table.country = selectedAreaCode.country;
          }

          if (
            nameInput.current?.value &&
            (webStoreVoResponse.data.webStoreVo.webUiConfig?.showInputName ||
              webStoreVoResponse.data.webStoreVo.webUiConfig?.enableInputName)
          ) {
            newOrderVo.customerName = nameInput.current.value;
          }
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          if (ctaBtnStyle) {
            submitOrder(webVo);
          }
          return;
        }
        if (
          selectedServeType === 'TAKE_OUT' &&
          selectedTakeOutServeType === 'SCHEDULE_TAKE_OUT' &&
          isPhoneValid(true) &&
          isDateTimeValid()
        ) {
          newOrderVo.serveType = 'TAKE_OUT';
          if (customizedServeType !== 'EMPLOYEE') {
            const phoneNumber =
              selectedAreaCode.areaCode + phoneInput.current.value;
            const parsePhone = parsePhoneNumber(
              phoneNumber,
              selectedAreaCode.iti
            );
            newOrderVo.table.mobile = parsePhone
              .formatInternational()
              .replaceAll(' ', '');
            newOrderVo.table.country = selectedAreaCode.country;
          }
          newOrderVo.preorderTime = selectedTime;
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          submitOrder(webVo);
          return;
        }
        if (
          selectedServeType === 'DELIVERY' &&
          isPhoneValid(true) &&
          isDeliveryFieldValid() &&
          isDateTimeValid()
        ) {
          newOrderVo.serveType = 'DELIVERY';

          const phoneNumber =
            selectedAreaCode.areaCode + phoneInput.current.value;
          const parsePhone = parsePhoneNumber(
            phoneNumber,
            selectedAreaCode.iti
          );
          newOrderVo.table.mobile = parsePhone
            .formatInternational()
            .replaceAll(' ', '');
          newOrderVo.table.country = selectedAreaCode.country;
          newOrderVo.customerName = nameInput.current.value;
          newOrderVo.customerAddress = getCustomerAddress();

          if (commentInput.current?.value !== undefined) {
            if (
              commentInput.current.value.length > 0 &&
              webStoreVoResponse.data.webStoreVo.webUiConfig
                ?.showRequireComments
            ) {
              newOrderVo.comment = commentInput.current.value;
            }
          }

          newOrderVo.preorderTime = selectedTime;
          newOrderVo.deliverableAction = getUberDeliveryMethod();
          newOrderVo.structuredAddress = getStructAddress();
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          if (ctaBtnStyle) {
            submitOrder(webVo);
          }

          return;
        }
      }
      if (createOrderType === 'EXISTING') {
        newOrderVo.orderType = 'EXISTING';
        if (selectedServeType === 'DINE_IN' && isPhoneValid(true)) {
          newOrderVo.serveType = 'DINE_IN';
          const phoneNumber =
            selectedAreaCode.areaCode + phoneInput.current.value;
          const parsePhone = parsePhoneNumber(
            phoneNumber,
            selectedAreaCode.iti
          );
          newOrderVo.table.mobile = parsePhone
            .formatInternational()
            .replaceAll(' ', '');
          newOrderVo.table.country = selectedAreaCode.country;
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          submitOrder(webVo);
          return;
        }
      }
      if (createOrderType === 'ADDON') {
        newOrderVo.orderType = 'ADDON';
        Object.keys(shareConditionInfo).forEach((key) => {
          newOrderVo.conditions[shareConditionInfo[key].id] =
            shareConditionInfo[key].count;
        });
        if (selectedServeType === 'DINE_IN' && isPhoneValid(false)) {
          if (phoneInput.current.value.length > 0) {
            const phoneNumber =
              selectedAreaCode.areaCode + phoneInput.current.value;
            const parsePhone = parsePhoneNumber(
              phoneNumber,
              selectedAreaCode.iti
            );
            newOrderVo.table.mobile = parsePhone
              .formatInternational()
              .replaceAll(' ', '');
            newOrderVo.table.country = selectedAreaCode.country;
          }
          newOrderVo.serveType = 'DINE_IN';
          webVo.orderVo = newOrderVo;
          webStoreVoResponse.data.webStoreVo.langs.map((lang: any) => {
            Object.keys(availableLocales).map((key) => {
              if (availableLocales[key].lang === lang && key === locale) {
                newOrderVo.menuLocale = lang;
              }
              return false;
            });
            return false;
          });
          submitOrder(webVo);
        }
      }
    }
  };

  const pushToSearchOrder = () => {
    sessionStorage.setItem('previousUrl', JSON.stringify(location));
    const queryParams = new URLSearchParams(location.search);
    let autoSearch = phoneInput.current?.value;
    if (customizedServeType === 'EMPLOYEE') {
      autoSearch = employeeInput.current?.value;
      queryParams.set('serveType', customizedServeType);
    }
    if (autoSearch) queryParams.set('autoSearch', autoSearch);
    if (selectedAreaCode.iti) queryParams.set('areaCode', selectedAreaCode.iti);
    queryParams.set('langs', webStoreVoResponse.data.webStoreVo.langs);
    history.push({
      pathname: `${process.env.REACT_APP_VERSION}/online/store/${storeUuid}/searchOrder`,
      search: `${queryParams}`,
      state: location.pathname,
    });
  };

  return (
    <div>
      {webStoreVoResponse?.code === ResponseCode.STORE_OFF_LINE && (
        <WelcomeServiceSuspended />
      )}
      {showCustomizeWelcome &&
        webStoreVoResponse?.code !== ResponseCode.STORE_OFF_LINE &&
        webStoreVoResponse &&
        webStoreVoResponse.data &&
        storeLocales &&
        storeLocales.length > 0 && (
          <CustomizeWelcome
            locale={locale}
            setLocale={setLocale}
            isTest={isTest}
          />
        )}

      {!showCustomizeWelcome &&
      webStoreVoResponse?.code !== ResponseCode.STORE_OFF_LINE &&
      webStoreVoResponse &&
      webStoreVoResponse.data &&
      storeLocales &&
      storeLocales.length > 0 ? (
        <div>
          <Privacy
            storeDisplay={webStoreVoResponse.data.webStoreVo.displayName}
          />
          <div
            className="modal fade"
            id="setPartyModal"
            tabIndex={-1}
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            aria-labelledby="setPartyModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ justifyContent: 'center !important' }}
                >
                  <h5 className="modal-title " id="setPartyModalLabel">
                    <FormattedMessage id="welcome.setPartyTitle" />
                  </h5>
                </div>
                <div className="modal-body">
                  {Object.entries(shareConditionInfo)
                    .sort(([i, a], [j, b]) => {
                      const x: any = a;
                      const y: any = b;
                      return x.idx - y.idx;
                    })
                    .map(([key, a]) => {
                      const x: any = a;
                      return (
                        <div key={x.id}>
                          <div className="d-flex justify-content-between">
                            <div className="d-flex flex-column">
                              <div>{shareConditionInfo[key].title}</div>
                              <div>{shareConditionInfo[key].desc}</div>
                            </div>
                            <div className="align-self-center">
                              {shareConditionInfo[key].count === 0 ? (
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const sInfo = JSON.parse(
                                        JSON.stringify(shareConditionInfo)
                                      );
                                      sInfo[key].count = 1;
                                      setShareConditionInfo(sInfo);
                                    }}
                                    className="btn"
                                    style={{
                                      color: '#272727',
                                      backgroundColor: '#fff',
                                      border: '2px solid rgba(0, 0, 0, 0.2)',
                                      borderRadius: '30px',
                                      width: '108px',
                                    }}
                                  >
                                    <FormattedMessage id="welcome.add" />
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className="align-self-center"
                                  style={{
                                    border: '2px solid rgba(0, 0, 0, 0.2)',
                                    borderRadius: '30px',
                                    width: '108px',
                                    height: '40px',
                                  }}
                                >
                                  <div
                                    className="d-flex justify-content-between"
                                    style={{ marginTop: '4px' }}
                                  >
                                    <img
                                      onClick={() => {
                                        const sInfo = JSON.parse(
                                          JSON.stringify(shareConditionInfo)
                                        );
                                        sInfo[key].count -= 1;
                                        setShareConditionInfo(sInfo);
                                      }}
                                      style={{
                                        maxHeight: '28px',
                                        paddingLeft: '5px',
                                      }}
                                      src={img.icon.minusSecondary}
                                      alt=""
                                    />
                                    <div>{shareConditionInfo[key].count}</div>
                                    <img
                                      onClick={() => {
                                        const sInfo = JSON.parse(
                                          JSON.stringify(shareConditionInfo)
                                        );
                                        sInfo[key].count += 1;
                                        setShareConditionInfo(sInfo);
                                      }}
                                      style={{
                                        maxHeight: '28px',
                                        paddingRight: '5px',
                                      }}
                                      src={img.icon.plusSecondary}
                                      alt=""
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            style={{
                              borderTop: '1px solid #BBBABA',
                              marginTop: '5px',
                              marginBottom: '5px',
                            }}
                          />
                        </div>
                      );
                    })}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    style={style.ctaBtn()}
                    className="btn party-confirm"
                    data-bs-dismiss="modal"
                    data-bs-target="#setPartyModal"
                  >
                    <FormattedMessage id="common.confirm" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            <div
              className="d-flex mx-auto"
              style={{
                width: 'calc(345px*0.7)',
                height: 'calc(210px*0.7)',
                backgroundColor: JSON.parse(
                  webStoreVoResponse.data.webStoreVo.uiConfig
                ).maColorPrimary,
                borderRadius: '5px',
                marginTop: '30px',
              }}
            >
              <img
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
                className="mx-auto align-items-center align-self-center"
                src={
                  webStoreVoResponse.data.webStoreVo.brandImageUrl
                    ? webStoreVoResponse.data.webStoreVo.brandImageUrl
                    : img.icon.maIcon
                }
                alt=""
              />
            </div>
            {webStoreVoResponse.data.webStoreVo.availableServeTypes.length >
              0 && webStoreVoResponse.data.webStoreVo.isOnlineOrderEnable ? (
              <div style={{ marginLeft: '30px', marginRight: '30px' }}>
                <div className="text-center">
                  <div className="dropdown">
                    <button
                      style={{
                        backgroundImage: currentStoreLocale
                          ? `url(${img.flag[currentStoreLocale.iti?.country]})`
                          : '',
                        backgroundRepeat: 'no-repeat',
                        paddingLeft: '40px',
                        backgroundSize: '3vh',
                        backgroundPosition: '5% 50%',
                        width: '225px',
                        height: '44px',
                        fontSize: '16px',
                      }}
                      className="btn btn-light dropdown-toggle btn-lg"
                      type="button"
                      data-testid="localeDropDownMenu"
                      id="localeDropDownMenu"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {currentStoreLocale ? currentStoreLocale.display : ''}
                    </button>
                    <ul
                      style={{ width: '225px' }}
                      className="dropdown-menu"
                      aria-labelledby="localeDropDownMenu"
                    >
                      {storeLocales.map((storeLocale, i) => {
                        const key = Object.keys(storeLocale)[0];
                        const { display } = storeLocale[key];
                        return (
                          <li key={i}>
                            <button
                              type="button"
                              style={{
                                backgroundImage: `url(${
                                  img.flag[storeLocale[key].iti?.country]
                                })`,
                                backgroundRepeat: 'no-repeat',
                                paddingLeft: '10vw',
                                backgroundSize: '3vh',
                                backgroundPosition: '5% 50%',
                                height: '6vh',
                                fontSize: '16px',
                              }}
                              className="dropdown-item btn-lg text-center"
                              onClick={() => {
                                setLocale(key);
                                const queryParams = new URLSearchParams(
                                  location.search
                                );
                                queryParams.set('welcomeLocale', String(key));

                                history.push({
                                  pathname: location.pathname,
                                  search: `${queryParams}`,
                                });
                              }}
                            >
                              {display}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                {webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE ? (
                  <ServeTypeRow />
                ) : (
                  <div
                    className="d-flex justify-content-around"
                    style={{
                      backgroundColor: `${style.getUiConfig().secondaryColor}`,
                      color: `${style.getUiConfig().whiteColor}`,
                      padding: '0.7rem 1rem',
                      borderRadius: '0.5rem',
                      fontSize: '18px',
                      marginTop: '40px',
                    }}
                  >
                    <FormattedMessage id="welcome.machine.offline" />
                  </div>
                )}

                {((webStoreVoResponse.data.webStoreVo.isRoyaltyEnable ||
                  webStoreVoResponse.data.webStoreVo.isPostPayEnable ||
                  webStoreVoResponse.data.webStoreVo.webUiConfig
                    ?.showInputPhone ||
                  webStoreVoResponse.data.webStoreVo.webUiConfig
                    ?.enableInputPhone) &&
                  selectedServeType === 'DINE_IN' &&
                  webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE) ||
                (selectedServeType === 'TAKE_OUT' &&
                  webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE) ||
                (selectedServeType === 'DELIVERY' &&
                  webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE) ||
                (webStoreVoResponse.data.webStoreVo.isRoyaltyEnable &&
                  (webStoreVoResponse.data.webStoreVo.webUiConfig
                    ?.showInputPhone ||
                    webStoreVoResponse.data.webStoreVo.webUiConfig
                      ?.enableInputPhone) &&
                  webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE) ? (
                  <PhoneInput
                    selectedAreaCode={selectedAreaCode}
                    areaCodes={areaCodes}
                    setSelectedAreaCode={setSelectedAreaCode}
                    phoneRef={phoneInput}
                    employeeRef={employeeInput}
                    checkCTABtnStatus={checkCTABtnStatus}
                  />
                ) : (
                  ''
                )}

                {selectedServeType === 'TAKE_OUT' &&
                webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE ? (
                  <div>
                    {selectedTakeOutServeType === 'TAKE_OUT' &&
                    (webStoreVoResponse.data.webStoreVo.webUiConfig
                      ?.showInputName ||
                      webStoreVoResponse.data.webStoreVo.webUiConfig
                        ?.enableInputName) ? (
                      <NameInput
                        name={name}
                        setName={setName}
                        nameRef={nameInput}
                        required={nameInputRequired}
                      />
                    ) : (
                      ''
                    )}
                    <div className="mt-3">
                      <button
                        className="btn btn-lg takeout"
                        style={
                          selectedTakeOutServeType === 'DEFAULT'
                            ? {
                                border: `1px solid ${
                                  style.getUiConfig().gray3Color
                                }`,
                                color: `${style.getUiConfig().gray3Color}`,
                                marginTop: '10px',
                              }
                            : {
                                border: `1px solid ${
                                  style.getUiConfig().gray3Color
                                }`,
                                color: `${style.getUiConfig().blackColor}`,
                                marginTop: '10px',
                              }
                        }
                        type="button"
                        data-testid="takeoutDropDownMenu"
                        id="takeoutDropDownMenu"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <FormattedMessage
                          id={`welcome.availableTakeOutServeTypes.${selectedTakeOutServeType}`}
                        />
                      </button>
                      <ul
                        style={{
                          width: '100%',
                          border: `1px solid ${style.getUiConfig().gray3Color}`,
                        }}
                        className="dropdown-menu takeout-list"
                        aria-labelledby="takeoutDropDownMenu"
                      >
                        {webStoreVoResponse.data.webStoreVo.availableTakeOutServeTypes.map(
                          (serveType: OrderServeType, i: number) => (
                            <li key={i}>
                              <button
                                type="button"
                                className="dropdown-item btn-lg text-center"
                                style={{ fontSize: '16px', height: '45px' }}
                                onClick={() => {
                                  setSelectedTakeOutServeType(serveType);
                                }}
                              >
                                <FormattedMessage
                                  id={`welcome.availableTakeOutServeTypes.${serveType}`}
                                />
                              </button>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                ) : (
                  ''
                )}
                {selectedServeType === 'DELIVERY' && (
                  <div>
                    <NameInput
                      name={name}
                      setName={setName}
                      nameRef={nameInput}
                      required={nameInputRequired}
                    />
                    {!webStoreVoResponse.data.webStoreVo.tradingLocations &&
                      !deliveryType && (
                        <AddressInput
                          addressRef={addressInput}
                          checkCTABtnStatus={checkCTABtnStatus}
                        />
                      )}
                  </div>
                )}

                {selectedServeType === 'DELIVERY' ||
                (selectedServeType === 'TAKE_OUT' &&
                  selectedTakeOutServeType === 'SCHEDULE_TAKE_OUT') ? (
                  <div className="mt-3">
                    <TimeSelector
                      minDate={minDate}
                      maxDate={maxDate}
                      excludeDates={excludeDates}
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                      availableTimes={availableTimes}
                      setAvailableTimes={setAvailableTimes}
                      selectedTime={selectedTime}
                      setSelectedTime={setSelectedTime}
                      timeInfos={timeInfos}
                      setTimeInfos={setTimeInfos}
                      selectedServeType={selectedServeType}
                      webStoreVo={webStoreVoResponse?.data?.webStoreVo}
                    />
                  </div>
                ) : (
                  ''
                )}

                {selectedServeType === 'DELIVERY' && deliveryType ? (
                  <div>
                    {/* 輸入 外送地址 title */}
                    <div
                      className="d-flex gap-1 align-items-center"
                      style={{
                        color: blackColor,
                        fontSize: '14px',
                        marginTop: '10px',
                      }}
                    >
                      <FormattedMessage id="delivery.please.input.delivery.address" />
                    </div>
                    <AddressSelector
                      city={city}
                      setCity={setCity}
                      area={area}
                      setArea={setArea}
                    />
                    <AddressDetails
                      addressRef={addressInput}
                      street={street}
                      setStreet={setStreet}
                      selectedServeType={selectedServeType}
                    />
                    <DeliveryMethodSelector
                      deliveryMethod={deliveryMethod}
                      setDeliveryMethod={setDeliveryMethod}
                    />
                  </div>
                ) : (
                  ''
                )}

                {webStoreVoResponse.data.webStoreVo.tradingLocations &&
                  selectedServeType === 'DELIVERY' &&
                  !deliveryType && (
                    <div className="mt-3">
                      <LocationSelector
                        locations={
                          webStoreVoResponse.data.webStoreVo.tradingLocations
                        }
                        tradingLocation={tradingLocation}
                        setTradingLocation={setTradingLocation}
                      />
                    </div>
                  )}
                {selectedServeType === 'DELIVERY' &&
                  !deliveryType &&
                  webStoreVoResponse.data.webStoreVo.webUiConfig
                    ?.showRequireComments === true && (
                    <CommentInput commentRef={commentInput} />
                  )}

                {showStoreNotOpenRemind() ? (
                  <StoreNotOpenRemind
                    webStoreVo={webStoreVoResponse.data.webStoreVo}
                  />
                ) : (
                  ''
                )}

                <TimeRemind webStoreVo={webStoreVoResponse.data.webStoreVo} />

                {selectedServeType === 'DINE_IN' &&
                webStoreVoResponse.data.webStoreVo.shareConditionInfo.length >
                  0 &&
                webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE ? (
                  <div className="mt-3 d-flex justify-content-center">
                    <div className="btn-group" style={{ width: '100%' }}>
                      <button
                        type="button"
                        className="btn btn-lg"
                        style={style.primaryBtnSelected()}
                        data-bs-toggle="modal"
                        data-bs-target="#setPartyModal"
                      >
                        <Components.DisplayParty />
                      </button>
                    </div>
                  </div>
                ) : (
                  ''
                )}
                {selectedServeType === 'DINE_IN' &&
                !webStoreVoResponse.data.webStoreVo.isPostPayEnable &&
                webStoreVoResponse.data.webStoreVo.shareConditionInfo.length >
                  0 &&
                webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE ? (
                  <div className="mt-3 d-flex justify-content-center">
                    <div
                      role="button"
                      className="btn-group"
                      style={{ width: '100%' }}
                      onClick={() => validateOrder('ADDON')}
                    >
                      <label
                        className="btn btn-lg"
                        style={style.primaryBtnSelected()}
                        data-testid="welcome_skipParty"
                      >
                        <FormattedMessage id="welcome.skipParty" />
                      </label>
                    </div>
                  </div>
                ) : (
                  ''
                )}
                {webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE ? (
                  webStoreVoResponse.data.webStoreVo.isPostPayEnable &&
                  selectedServeType === 'DINE_IN' ? (
                    <div className="mt-5 d-flex justify-content-center">
                      <div
                        role="button"
                        className="mx-2"
                        onClick={() => validateOrder('EXISTING')}
                      >
                        <label
                          className="btn btn-lg"
                          style={style.ctaBtn()}
                          data-testid="post_welcome_existingorder"
                        >
                          <FormattedMessage id="welcome.existingOrder" />
                        </label>
                      </div>
                      <div
                        role="button"
                        className="mx-2"
                        onClick={() => validateOrder('NEW')}
                      >
                        <label
                          className="btn btn-lg"
                          style={
                            !ctaBtnStyle
                              ? style.ctaBtnDisable()
                              : style.ctaBtn()
                          }
                          data-testid="post_welcome_neworder"
                        >
                          <FormattedMessage id="welcome.newOrder" />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div
                        className="d-flex justify-content-center"
                        style={{ marginTop: '20px' }}
                      >
                        <div
                          role="button"
                          className="btn-group"
                          style={{ width: '100%' }}
                          onClick={() => validateOrder('NEW')}
                        >
                          <label
                            className="btn btn-lg"
                            style={
                              !ctaBtnStyle
                                ? style.ctaBtnDisable()
                                : style.ctaBtn()
                            }
                            data-testid="welcome_neworder"
                          >
                            {ctaBtnText ? (
                              <FormattedMessage id="welcome.newOrder" />
                            ) : (
                              <FormattedMessage id="welcome.viewMenu" />
                            )}
                          </label>
                        </div>
                      </div>

                      {!webStoreVoResponse.data.webStoreVo.isPostPayEnable ? (
                        <div
                          className="d-flex justify-content-center"
                          style={{ marginTop: '20px' }}
                        >
                          <div
                            role="button"
                            className="btn-group"
                            style={{ width: '100%' }}
                            onClick={() => {
                              pushToSearchOrder();
                            }}
                          >
                            <label
                              className="btn btn-lg"
                              style={style.searchOrderBtn()}
                              data-testid="order_search"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                fill="currentColor"
                                className="bi bi-search"
                                viewBox="0 0 16 16"
                                style={{ margin: '0px 10px 0px 0px' }}
                              >
                                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                              </svg>
                              {webStoreVoResponse?.data?.webStoreVo
                                ?.isRoyaltyEnable ? (
                                <FormattedMessage id="welcome.search.order.and.redeem" />
                              ) : (
                                <FormattedMessage id="common.searchOrder" />
                              )}
                            </label>
                          </div>
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                  )
                ) : (
                  ''
                )}
                {webStoreVoResponse.data.webStoreVo.isPostPayEnable &&
                selectedServeType === 'DINE_IN' &&
                webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE ? (
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      marginTop: '20px',
                      color: style.getUiConfig().secondaryColor,
                      fontSize: '12px',
                    }}
                  >
                    <FormattedMessage id="welcome.postpay" />
                  </div>
                ) : webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE &&
                  webStoreVoResponse.data.webStoreVo.isRoyaltyEnable &&
                  (selectedServeType === 'DINE_IN' ||
                    (selectedServeType === 'TAKE_OUT' &&
                      selectedTakeOutServeType !== 'SCHEDULE_TAKE_OUT')) ? (
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      marginTop: '20px',
                      color: style.getUiConfig().secondaryColor,
                      fontSize: '12px',
                    }}
                  >
                    <FormattedMessage id="welcome.royality" />
                  </div>
                ) : webStoreVoResponse.code === ResponseCode.STORE_OFF_LINE ? (
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      marginLeft: '10px',
                      marginTop: '30px',
                      color: 'red',
                      fontSize: '15px',
                    }}
                  >
                    <FormattedMessage id="welcome.machine.offline.text" />
                  </div>
                ) : (
                  ''
                )}
              </div>
            ) : (
              <div
                className="d-flex justify-content-center"
                style={
                  webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE
                    ? {
                        marginTop: '180px',
                        color: 'red',
                        fontSize: '25px',
                        letterSpacing: '2vw',
                        marginLeft: '8%',
                        marginRight: '8%',
                        textAlign: 'left',
                      }
                    : {
                        marginTop: '180px',
                        color: 'red',
                        fontSize: '15px',
                        marginLeft: '8%',
                        marginRight: '8%',
                        textAlign: 'left',
                      }
                }
              >
                {webStoreVoResponse.code !== ResponseCode.STORE_OFF_LINE ? (
                  <FormattedMessage id="error.40011" />
                ) : (
                  <FormattedMessage id="welcome.machine.offline.text" />
                )}
              </div>
            )}
          </div>
          <div
            className="sticky-top"
            style={{
              marginTop: '125px',
              zIndex: 0,
            }}
          >
            <BottomStoreInfo
              webStoreVo={webStoreVoResponse.data.webStoreVo}
              style={style}
            />
          </div>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

Welcome.defaultProps = {
  isTest: false,
};
export default Welcome;
